// Recalibration client store. Mirrors the server-authoritative schedule
// and derives status/countdown for UI. Never mutates server state
// directly — all writes happen through callable Cloud Functions.
//
// Source: CogniPrint_Recalibration_Dev_Plan_v1.md §3.1 (REC-010).

import { create } from 'zustand';
import type { RecalibrationSchedule, RecalibrationStatus } from '../types/recalibration';
import {
  deriveRecalibrationStatus,
  daysUntilRecalibration,
} from '../services/recalibration-cadence';

interface RecalibrationState {
  schedule: RecalibrationSchedule | null;
  status: RecalibrationStatus | null;
  daysUntil: number | null;
  loading: boolean;
  error: string | null;

  setSchedule: (schedule: RecalibrationSchedule | null) => void;
  refreshDerivations: (now?: Date) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

function derive(schedule: RecalibrationSchedule | null, now: Date = new Date()) {
  if (!schedule) {
    return { status: null, daysUntil: null };
  }
  return {
    status: deriveRecalibrationStatus(schedule, now),
    daysUntil: daysUntilRecalibration(schedule.nextRecalibration, now),
  };
}

export const useRecalibrationStore = create<RecalibrationState>((set) => ({
  schedule: null,
  status: null,
  daysUntil: null,
  loading: false,
  error: null,

  setSchedule: (schedule) => {
    const { status, daysUntil } = derive(schedule);
    set({ schedule, status, daysUntil, error: null });
  },

  refreshDerivations: (now = new Date()) => {
    set((state) => {
      if (!state.schedule) return state;
      const { status, daysUntil } = derive(state.schedule, now);
      return { ...state, status, daysUntil };
    });
  },

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  reset: () =>
    set({
      schedule: null,
      status: null,
      daysUntil: null,
      loading: false,
      error: null,
    }),
}));
